import { Grid, Typography, Stack, Chip, Tooltip } from '@mui/material';

import { ChatAssistantDataProps } from './props';
import FieldText from '../../../../components/general/fields/fieldText';
import AutocompleteField from '../../../../components/general/fields/autocompleteField';
import DropZoneField from '../../../../components/general/fields/dropzoneField';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Table } from '../../../../components/general/table';
import { Checkbox } from '../../../../components/general/fields/checkbox';
import LoadingButtonGeneral from '../../../../components/general/buttons/loadingButtonGeneral';
import DeleteIcon from '@mui/icons-material/Delete';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import FolderIcon from '@mui/icons-material/Folder';
import { FileExplorer } from '../../../../components/chat-gpt/sharepoint/FileExplorer';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { BasicCard } from '../../../../components/general/card/BasicCard';
import MultipleAutocompleteField from '../../../../components/general/fields/multipleAutocompleteField';

function CollapsibleChips({
    items,
    onRemove,
    maxVisible = 6,
}: {
    items: { id: string; name: string; isFolder?: boolean }[];
    onRemove: (id: string) => void;
    maxVisible?: number;
}) {
    const visible = items.slice(0, maxVisible);
    const hidden = items.length - visible.length;

    return (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.5}>
            {visible.map(({ id, name, isFolder }) => (
                <Tooltip title={name} key={id}>
                    <Chip
                        icon={isFolder ? <FolderIcon /> : <AttachFileIcon />}
                        label={
                            <Typography
                                noWrap
                                sx={{ maxWidth: 160, fontSize: 12 }}
                            >
                                {name}
                            </Typography>
                        }
                        onDelete={() => onRemove(id)}
                        sx={{
                            borderRadius: 2,
                            '& .MuiChip-icon': { fontSize: 18 },
                            bgcolor: 'background.paper',
                        }}
                        variant="outlined"
                        size="small"
                    />
                </Tooltip>
            ))}
            {hidden > 0 && (
                <Chip
                    label={`+${hidden} más`}
                    size="small"
                    variant="outlined"
                    sx={{ borderStyle: 'dashed' }}
                />
            )}
            {items.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No hay elementos seleccionados.
                </Typography>
            )}
        </Stack>
    );
}

export const ChatAssistantData = (props: ChatAssistantDataProps) => {
    const {
        formik,
        rows,
        projects,
        handleOnChange,
        columns,
        isApiKeyFounded,
        isSearched,
        paginatedRows,
        paginationModel,
        handlePaginationChange,
        handleSelectionChange,
        selectionModel,
        handleDeleteMultiple,
        isSuperAdmin,
        connectToSharepoint,
        isSharepointAuth,
        openSharepointSelector,
        sharepointModalOpen,
        setSharepointModalOpen,
        sharepointItems,
        setSharepointItems,
        setIsSharepointAuth,
        users,
        isAssistantOwnerOrAdmin,
    } = props;

    return (
        <Grid
            container
            spacing={2}
            columnSpacing={{ xs: 2, sm: 2, md: 2 }}
            pt="20px"
        >
            {/* Datos básicos */}
            <Grid item xs={12} sm={6}>
                <FieldText
                    label="Nombre"
                    name="name"
                    size="small"
                    required
                    onBlur={formik?.handleBlur}
                    touched={formik?.touched.name}
                    errors={formik?.errors.name}
                    value={formik?.values.name}
                    onChange={formik?.handleChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FieldText
                    label="Nombre a mostrar"
                    name="displayName"
                    size="small"
                    required
                    onBlur={formik?.handleBlur}
                    touched={formik?.touched.displayName}
                    errors={formik?.errors.displayName}
                    value={formik?.values.displayName}
                    onChange={formik?.handleChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <AutocompleteField
                    data={projects}
                    label="Proyecto"
                    name="project"
                    size="small"
                    required
                    onBlur={formik?.handleBlur}
                    touched={formik?.touched.project}
                    errors={formik?.errors.project}
                    value={formik?.values.project}
                    onChangeValue={(value: any) =>
                        formik?.setFieldValue('project', value)
                    }
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <MultipleAutocompleteField
                    data={users}
                    label="Gestores autorizados"
                    name="authorizedManagers"
                    size="small"
                    touched={formik?.touched.authorizedManagers}
                    errors={formik?.errors.authorizedManagers}
                    value={formik?.values.authorizedManagers}
                    helperText="Usuarios que pueden visualizar y/o modificar el asistente según su rol"
                    onChangeValue={(newValue) =>
                        formik?.setFieldValue('authorizedManagers', newValue)
                    }
                    disabled={!isAssistantOwnerOrAdmin}
                />
            </Grid>
            <Grid item xs={12}>
                <FieldText
                    label="Descripción"
                    name="description"
                    size="small"
                    rows={3}
                    required
                    onBlur={formik?.handleBlur}
                    touched={formik?.touched.description}
                    errors={formik?.errors.description}
                    value={formik?.values.description}
                    onChange={formik?.handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <FieldText
                    label="API KEY IA"
                    name="apiKeyIa"
                    size="small"
                    required={isSearched && !isApiKeyFounded}
                    onBlur={formik?.handleBlur}
                    touched={formik?.touched.apiKeyIa}
                    errors={formik?.errors.apiKeyIa}
                    value={formik?.values.apiKeyIa}
                    onChange={formik?.handleChange}
                    disabled={!isSearched || !(isSearched && !isApiKeyFounded)}
                />
            </Grid>
            <Grid item xs={12}>
                <Checkbox
                    label="Activar modo avanzado (permite elegir entre modo básico o avanzado al crear cada chat. El modo avanzado admite archivos adjuntos y generación de archivos en la conversación.)"
                    onBlur={formik?.handleBlur}
                    checked={Boolean(formik?.values.isAdvancedMode)}
                    errors={formik?.errors.isAdvancedMode}
                    onChange={() =>
                        formik?.setFieldValue(
                            'isAdvancedMode',
                            !formik.values.isAdvancedMode
                        )
                    }
                    isCheckMode
                />
            </Grid>
            {isSuperAdmin && (
                <Grid item xs={12}>
                    <Checkbox
                        label="Integración API (distintivo exclusivo para tareas de integración técnica vía API. No visible para otros perfiles)"
                        onBlur={formik?.handleBlur}
                        checked={Boolean(formik?.values.apiIntegration)}
                        errors={formik?.errors.apiIntegration}
                        onChange={() => {
                            formik?.setFieldValue(
                                'apiIntegration',
                                !formik.values.apiIntegration
                            );
                        }}
                        isCheckMode
                    />
                </Grid>
            )}

            {/* SharePoint */}
            <Grid item xs={12}>
                <BasicCard
                    title="Agregar desde SharePoint"
                    subheader={
                        isSharepointAuth
                            ? 'Selecciona archivos o carpetas'
                            : 'Conecta tu cuenta para explorar archivos'
                    }
                    action={
                        isSharepointAuth ? (
                            <LoadingButtonGeneral
                                title="Seleccionar desde SharePoint"
                                onClick={openSharepointSelector}
                                startIcon={<FolderIcon />}
                            />
                        ) : (
                            <LoadingButtonGeneral
                                title="Conectar con SharePoint"
                                onClick={connectToSharepoint}
                                startIcon={<MicrosoftIcon />}
                            />
                        )
                    }
                    contentSx={{ pt: 0 }}
                >
                    <CollapsibleChips
                        items={sharepointItems}
                        onRemove={(id) =>
                            setSharepointItems((prev) =>
                                prev.filter((i) => i.id !== id)
                            )
                        }
                        maxVisible={6}
                    />
                </BasicCard>
            </Grid>

            {/* Dropzone */}
            <Grid item xs={12}>
                <BasicCard title="Subir archivos locales">
                    <DropZoneField
                        name="attachments"
                        title="Suelta los archivos aquí o haz clic para subir"
                        subtitle={null}
                        subtitle2={null}
                        maxFileSize={30 * 1024 * 1024}
                        onChange={handleOnChange}
                        accept=".doc, .docx, .pdf, .pptx, .txt, .xlsx"
                        autoClean={false}
                    >
                        <CloudUploadIcon fontSize="large" color="warning" />
                    </DropZoneField>
                </BasicCard>
            </Grid>

            {/* Files */}
            {rows && rows.length > 0 && (
                <Grid item xs={12}>
                    <BasicCard
                        title={`Archivos (${rows.length})`}
                        action={
                            <LoadingButtonGeneral
                                title="Eliminar seleccionados"
                                disabled={selectionModel?.length === 0}
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteMultiple}
                                tooltip={
                                    selectionModel?.length === 0
                                        ? 'No hay archivos seleccionados'
                                        : undefined
                                }
                            />
                        }
                        withDivider
                        contentSx={{ pt: 0 }}
                    >
                        <Table
                            columns={columns}
                            rows={paginatedRows}
                            rowCount={rows.length}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationChange}
                            pageSizeOptions={[10, 20, 50]}
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={handleSelectionChange}
                            checkboxSelection
                        />
                    </BasicCard>
                </Grid>
            )}

            {/* Modal de SharePoint */}
            <FileExplorer
                open={sharepointModalOpen}
                onClose={() => setSharepointModalOpen(false)}
                selectedItems={sharepointItems}
                setIsSharepointAuth={setIsSharepointAuth}
                setSharepointItems={setSharepointItems}
                setSharepointModalOpen={setSharepointModalOpen}
            />
        </Grid>
    );
};
